import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useForm from '../../../../lib/hooks/useForm';
import useGetDeliver from './useGetDeliver';
import { saveDeliverDraft, sendTest, send } from './data';
import { useNotification } from '../../../../shared/Notification/useNotification';
import i18n from '../../../../app/i18n';

import { DeliverFormContext } from './useDeliverForm';

const headlines = {
  tileDigest: i18n.t('send:deliver.headlines.tileDigest'),
  followUp: i18n.t('send:deliver.headlines.followUp'),
};

export const DeliverFormProvider = ({
  emailPreviewPath,
  smsPreviewPath,
  ...props
}) => {
  const { t } = useTranslation(['send', 'boardSetup']);
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const iframeSrcs = {
    tileDigest: id =>
      `${emailPreviewPath}?follow_up_email=false&population_segment_id=${id}`,
    followUp: id =>
      `${emailPreviewPath}?follow_up_email=true&population_segment_id=${id}`,
    sms: id => `${smsPreviewPath}?population_segment_id=${id}`,
  };
  const iframeRef = useRef(null);
  const [iframe, setIframe] = useState('tileDigest');
  const deliver = useGetDeliver();
  const { mutate: saveMutation, isLoading: isSaving } = useMutation(
    saveDeliverDraft,
    {
      onSuccess: () => {
        showSuccess({
          content: t('send:deliver.form.save.success'),
          title: t('send:deliver.form.save.title'),
        });
      },
    },
  );
  const { mutate: sendTestMutation, isLoading: isSendingTest } = useMutation(
    sendTest,
    {
      onSuccess: ({ data }) => {
        showSuccess({
          content: data.send_test_message,
          title: t('send:deliver.form.sendTest.title'),
        });
      },
    },
  );

  const { mutate: sendMutation, isLoading: isSending } = useMutation(send, {
    onSuccess: ({ data }) => {
      queryClient.setQueryData(['deliver'], data);
      showSuccess({
        content: t('send:deliver.form.send.success'),
        title: t('send:deliver.form.send.title'),
      });
      iframeRef.current.contentWindow.location.reload();
    },
  });

  const initialValues = {
    demoId: deliver.demo_id,
    audiencePreview: '',
    audienceCounts: deliver.audience_counts,
    digestSendTo: deliver.digest_send_to || '',
    followUpDay: deliver.follow_up_day || '',
    includeSms: deliver.include_sms || false,
    customSubject: deliver.custom_subject || '',
    altCustomSubject: deliver.alt_custom_subject || '',
    customHeadline: deliver.custom_headline || '',
    customMessage: deliver.custom_message || '',
  };

  const form = useForm({
    initialValues,
    enableReinitialize: true,
  });

  const updateIframe = useCallback(() => {
    const customHeadline = iframeRef.current.contentDocument.querySelector(
      '#custom_headline',
    );
    const customMessage = iframeRef.current.contentDocument.querySelector(
      '#custom_message',
    );
    const customSubject = iframeRef.current.contentDocument.querySelector(
      '.subject-field',
    );
    if (customHeadline) {
      customHeadline.textContent =
        form.values.customHeadline || headlines[iframe];
    }
    if (customMessage) {
      customMessage.textContent = form.values.customMessage;
    }
    if (customSubject) {
      const boardName = customSubject.dataset.board;
      const subject =
        form.values.customSubject || t('send:deliver.emptySubjectDefault');
      customSubject.textContent = `${boardName}: ${subject}`;
    }
  }, [
    form.values.customHeadline,
    form.values.customMessage,
    form.values.customSubject,
    iframe,
    t,
  ]);

  const value = useMemo(
    () => ({
      ...form,
      iframeRef,
      iframeSrc: iframeSrcs[iframe](form.values.audiencePreview),
      iframe,
      setIframe,
      updateIframe,
      deliver,
      onSave: () => saveMutation(form.values),
      onSend: () => sendMutation(form.values),
      onSendTest: () => sendTestMutation(form.values),
      disabledSend: deliver.is_empty || isSending,
      disabledSendTest: deliver.is_empty || isSendingTest,
      disabledSave: deliver.is_empty || isSaving,
    }),
    [
      form,
      iframeSrcs,
      iframe,
      updateIframe,
      deliver,
      isSending,
      isSendingTest,
      isSaving,
      saveMutation,
      sendMutation,
      sendTestMutation,
    ],
  );

  useEffect(() => {
    if (iframeRef.current) {
      updateIframe();
    }
  }, [form.values, updateIframe]);

  return <DeliverFormContext.Provider value={value} {...props} />;
};
